import { OrganizationStatus } from '../enums';
import { EOrganization, ERate, ESnapshotExists } from '../types';
import {
  COLUMN_REP_FEE,
  ENOTICE_CONVENIENCE_FEE,
  ENOTICE_CONVENIENCE_FEE_GOVT
} from '../constants';
import { AdRate, isNoticeRate, isPercentAdditionalFee } from '../types/rates';
import {
  FeeSplit,
  isFeeSplitTypeFullWaiver,
  TrackedFeeSplitResult
} from '../types/feeSplit';
import { valueIsNumber } from './math';
import { NoticeAddOnPricing } from './noticeAddOns/types';
import { sumNoticeAddOnPricing } from './noticeAddOns/shared';

export const DEFAULT_COLUMN_FEE_PCT = 10;

/**
 * The subtotal the percentage convenience fee applies to: the line-item subtotal
 * minus notice add-on charges (affidavit/shipping), which are billed at face value
 * and must not have a convenience fee layered on top.
 */
export const getConvenienceFeeSubtotalInCents = (
  subtotalInCents: number,
  noticeAddOns: NoticeAddOnPricing | null | undefined
): number => Math.max(subtotalInCents - sumNoticeAddOnPricing(noticeAddOns), 0);

export type ConvenienceFee = {
  /** A flat fee in cents, charged in addition to the percentage fee */
  convenienceFeeFlat: number;
  /** A percentage fee, multipled by the publisher amount */
  convenienceFeePct: number;
  /** A cap on the total convenience fee in cents */
  convenienceFeeCap: number | undefined;
};

/**
 * Determine the fee percentage and cap for a given rate, considering first
 * if the rate is for governments or not.
 *
 * Note: this method is not suitable for calculating the final convenience fee
 * for a notice as it does not include the column rep fee which may be present
 * as an additional fee on the newspaper.
 */
export const rateToConvenienceFee = (rate: AdRate): ConvenienceFee => {
  if (!isNoticeRate(rate)) {
    return {
      convenienceFeePct: rate.enotice_fee_pct ?? ENOTICE_CONVENIENCE_FEE,
      convenienceFeeFlat: 0,
      convenienceFeeCap: undefined
    };
  }
  const { isGovernment, enotice_fee_pct, columnRepFeePct } = rate;

  const basePercentage = isGovernment
    ? ENOTICE_CONVENIENCE_FEE_GOVT
    : enotice_fee_pct ?? ENOTICE_CONVENIENCE_FEE;

  // We charge an additional fee if all of the below are true:
  //  - The rate has columnRepFeePct
  //  - The original percentage was > 0
  const additionalPercentage =
    !!columnRepFeePct && basePercentage ? columnRepFeePct : 0;

  const convenienceFeePct = basePercentage + additionalPercentage;

  return {
    convenienceFeePct,
    convenienceFeeFlat: rate.convenienceFeeFlat || 0,
    convenienceFeeCap: rate.convenienceFeeCap
  };
};

export const maybeGetColumnRepFeePctFromNewspaper = (
  newspaper: ESnapshotExists<EOrganization>
) => {
  const columnRepFee = newspaper
    .data()
    .additionalFees?.find(fee => fee.description === COLUMN_REP_FEE);

  return columnRepFee && isPercentAdditionalFee(columnRepFee)
    ? columnRepFee.feePercentage
    : undefined;
};

/**
 * Returns a ConvenienceFee with Column Rep fee:
 * - When it is a non-government rate, ex. the convenience fee on a rate > 0.
 * - In the case where there are rep fees on the rate and newspaper only one rep fee should be added.
 * - The rep fee on the rate overrides the rep fee on the newspaper.
 */
export const getConvenienceFeeWithRepFeeFromRateOrNewspaper = (
  newspaper: ESnapshotExists<EOrganization>,
  rate: ESnapshotExists<ERate>
): ConvenienceFee => {
  const convenienceFeeFromRate = rateToConvenienceFee(rate.data());

  // If either of the following is true, the rate controls the convenience fee:
  // 1) The rate has the base "convenienceFeePct" set to 0
  // 2) The rate has the "columnRepFeePct" set to > 0
  if (
    convenienceFeeFromRate.convenienceFeePct === 0 ||
    (convenienceFeeFromRate.convenienceFeePct > 0 &&
      rate.data().columnRepFeePct)
  ) {
    return convenienceFeeFromRate;
  }

  const convenienceFee = { ...convenienceFeeFromRate };
  const columnRepFeeFromNewspaper =
    maybeGetColumnRepFeePctFromNewspaper(newspaper);

  if (columnRepFeeFromNewspaper) {
    convenienceFee.convenienceFeePct += columnRepFeeFromNewspaper;
  }

  return convenienceFee;
};

/**
 * Calculate the convenience fee in cents.
 */
export const calculateConvenienceFeeAndFeeSplit = (
  subtotal: number,
  fee: ConvenienceFee,
  feeSplit: FeeSplit | undefined
): {
  convenienceFeeInCents: number;
  trackedConvenienceFeeSplitResult: TrackedFeeSplitResult | undefined;
} => {
  const { convenienceFeePct, convenienceFeeCap, convenienceFeeFlat } = fee;
  const convenienceFeeAmountInCentsNoSplit = Math.round(
    (subtotal * convenienceFeePct) / 100 + (convenienceFeeFlat || 0)
  );

  let convenienceFeeAmountInCents = convenienceFeeAmountInCentsNoSplit;

  const splitType = feeSplit?.type;

  const feeSplitTypeIsFullWaiver = isFeeSplitTypeFullWaiver(feeSplit);

  if (feeSplitTypeIsFullWaiver) {
    convenienceFeeAmountInCents = 0;
  }

  const splitAmount = feeSplit?.amount ?? 0;

  if (splitType === 'percent') {
    const splitCap = feeSplit?.cap;
    const initialSplitAmtInCents = Math.round(
      (splitAmount / 100) * convenienceFeeAmountInCents
    );
    const splitAmtInCents = valueIsNumber(splitCap)
      ? Math.min(splitCap, initialSplitAmtInCents)
      : initialSplitAmtInCents;
    convenienceFeeAmountInCents = Math.max(
      convenienceFeeAmountInCents - splitAmtInCents,
      0
    );
  }

  if (splitType === 'flat') {
    convenienceFeeAmountInCents = Math.max(
      convenienceFeeAmountInCents - splitAmount,
      0
    );
  }

  const cappedConvenienceFeeAmountInCents = convenienceFeeCap
    ? Math.min(convenienceFeeAmountInCents, convenienceFeeCap)
    : convenienceFeeAmountInCents;

  const feeSplitAmountInCents =
    convenienceFeeAmountInCentsNoSplit - cappedConvenienceFeeAmountInCents;

  const trackedConvenienceFeeSplitResult = !feeSplit
    ? undefined
    : {
        feeSplit,
        amountInCents: feeSplitTypeIsFullWaiver ? 0 : feeSplitAmountInCents,
        ...(feeSplitTypeIsFullWaiver && { amountWaived: feeSplitAmountInCents })
      };

  return {
    convenienceFeeInCents: cappedConvenienceFeeAmountInCents,
    trackedConvenienceFeeSplitResult
  };
};

export const getInvoiceConvenienceFee = (
  newspaper: ESnapshotExists<EOrganization>,
  advertiserOrganization: ESnapshotExists<EOrganization> | null,
  baseConvenienceFee: ConvenienceFee
): ConvenienceFee => {
  const shouldZeroOutConvenienceFee =
    newspaper.data().organizationStatus === OrganizationStatus.express.value &&
    advertiserOrganization?.data()?.expressPlacementBilling
      ?.overrideConvenienceFee;

  if (!shouldZeroOutConvenienceFee) {
    return baseConvenienceFee;
  }

  return {
    convenienceFeePct: 0,
    convenienceFeeFlat: 0,
    convenienceFeeCap: 0
  };
};

export const calculateConvenienceFee = (
  subtotal: number,
  fee: ConvenienceFee,
  feeSplit: FeeSplit | undefined
) => {
  const { convenienceFeeInCents } = calculateConvenienceFeeAndFeeSplit(
    subtotal,
    fee,
    feeSplit
  );
  return convenienceFeeInCents;
};
